import { MouseEvent, useCallback } from 'react'
import { ListItemButton, styled, Typography } from '@mui/material'
import { RemovePersonIcon } from '$common/icons/remove-person'
import { getUserFirstName, useUser } from '$logic/users'
import { getUserImageUrl } from '$func/imageUrl'
import { BadgeWrapper } from '$common/badge/styled-badge'
import { stringToColor } from '$func/string-to-color'
import { AvatarBase } from '$common/avatar/avatar-base'
import { getAvatarText } from '$func/avatar-text'
import { RecentUserItemSkeleton } from './recent-user-item-skeleton'
import { UserAvatar } from '$common/avatar'
import { AvatarSize } from '$common/avatar/avatar-base/types.ts'

const Root = styled(ListItemButton)`
  height: 96px;
  min-width: 82px;
  max-width: 82px;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0 5px;
  justify-content: space-between;
  box-sizing: border-box;
`

const StyledTypography = styled(Typography)`
  width: 100%;
  text-align: center;
`

interface Prop {
  userId: string
  selected?: boolean
  onSelectedChange?: (selected: boolean) => unknown
  hasSelection?: boolean
  onSelect?: () => unknown
}

export function RecentUserItem({
  userId,
  selected,
  hasSelection,
  onSelectedChange,
  onSelect,
}: Prop) {
  const { user, loading } = useUser(userId)

  const onClick = useCallback(() => {
    if (selected) onSelectedChange?.(false)
    else if (hasSelection) onSelectedChange?.(true)
    else if (onSelect) onSelect()
  }, [onSelect, selected, hasSelection, onSelectedChange])

  const onContextMenu = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      if (!hasSelection) onSelectedChange?.(true)
    },
    [hasSelection, onSelectedChange],
  )
  if (loading || !user) return <RecentUserItemSkeleton />

  const avatarSrc = getUserImageUrl(user)
  const avatarBackgroundColor = stringToColor(getUserFirstName(user), user?.id)
  return (
    <Root onClick={onClick} onContextMenu={onContextMenu}>
      <BadgeWrapper selected={!!selected}>
        <UserAvatar userId={userId} size={AvatarSize.LG} withStory />
      </BadgeWrapper>

      <StyledTypography variant="caption1" noWrap>
        {getUserFirstName(user)}
      </StyledTypography>
    </Root>
  )
}

RecentUserItem.defaultProps = {
  selected: false,
  hasSelection: false,
  onSelectedChange: null,
  onSelect: null,
}
